const config = {
  error_text_size: 12,
  error_color: 'red',
  error_class: 'error_class',
  error_display_html: true,
  text_min: 2,
  text_max: 50,
  highlight_all_errors: false,
};

const regexes = {
  email: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
  upi: /^[\w.-]+@[\w.-]+$/,
  ifsc: /^[A-Za-z]{4}\d{7}$/,
  exact_x_digits: (x) => new RegExp(`^[0][1-9]\\d{${x - 1}}$|^[1-9]\\d{${x - 1}}$`),
  any_number: (allowNegative, allowDecimal) => new RegExp(
    `^${allowNegative ? '([-+]?)' : ''}\\d+${allowDecimal ? '(\\.\\d+)?' : ''}$`
  ),
  text: (allowNumbers, otherChars, min, max) => new RegExp(
    `^[a-zA-Z${allowNumbers ? '0-9' : ''}${otherChars || ''}]{${min || config.text_min},${max || config.text_max}}$`
  ),
  filepath: /(\\\\?([^\\/]*[\\/])*)([^\\/]+)$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  gst: /^([0-2][0-9]|[3][0-7])[A-Z]{3}[ABCFGHLJPTK][A-Z]\d{4}[A-Z][A-Z0-9][Z][A-Z0-9]$/,
  decimal: /^\d+(\.\d{1,2})?$/,
  website_regex: /^((ftp|http|https):\/\/)[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/
};

const regexValidation = {
  email: (text) => regexes.email.test(text),
  upi: (text) => regexes.upi.test(text),
  ifsc: (text) => regexes.ifsc.test(text),
  exact_x_digits: (text, { x_count = 100 }) => regexes.exact_x_digits(x_count).test(text),
  any_number: (text, { allow_negative = false, allow_decimal = false }) => regexes.any_number(allow_negative, allow_decimal).test(text),
  text: (text, { allow_numbers = false, other_chars = '', min, max }) =>
    regexes.text(allow_numbers, other_chars, min, max).test(text),
  free_text: (text, { min = config.text_min, max = config.text_max }) =>
    text.length >= min && text.length <= max,
  min_number: (text, { min }) => {
    const value = Number(text);
    return !isNaN(value) && value >= min;
  },
  max_number: (text, { max }) => {
    const value = Number(text);
    return !isNaN(value) && value <= max;
  },
  min_max_number: (text, { min, max }) => {
    const value = Number(text);
    return !isNaN(value) && value >= min && value <= max;
  },
  equal: (text, { compareWith }) => text === compareWith,
  filepath: (text, { namecheck, checkstring }) => {
    const match = text.match(regexes.filepath);
    if (!match) return false;
    const filename = match[match.length - 1];
    switch (namecheck) {
      case 'startswith': return filename.startsWith(checkstring);
      case 'endswith': return filename.endsWith(checkstring);
      case 'contains': return filename.includes(checkstring);
      case 'exact': return filename === checkstring;
      case 'extension': return filename.endsWith(`.${checkstring}`);
      default: return true;
    }
  },
  pan_card: (text) => regexes.pan.test(text),
  gstin: (text, { statecode }) => {
    if (statecode && !text.startsWith(statecode)) return false;
    return regexes.gst.test(text);
  },
  custom: (text, { custom_regex }) => {
    if (!custom_regex) return false;
    return new RegExp(custom_regex).test(text);
  },
  decimal: (text) => regexes.decimal.test(text),
  website: (text) => regexes.website_regex.test(text),
};

export const validateInputs = (inputList, options = {}) => {
  const validationErrors = [];

  const mergedConfig = { ...config, ...options };

  for (const input of inputList) {
    const {
      tagid,
      value,
      required = false,
      regex_name,
      errmsg,
      custom_regex,
      ...rest
    } = input;

    const trimmed = String(value ?? '').trim();;
    if (!trimmed && !required) continue;

    const validator = regexValidation[regex_name];
    if (!validator) {
      console.warn(`Unknown regex_name: ${regex_name}`);
      continue;
    }

    const isValid = regex_name === 'custom'
      ? validator(trimmed, { ...rest, custom_regex })
      : validator(trimmed, rest);

    if (!isValid) {
      validationErrors.push({ tagid, errmsg });
      if (!mergedConfig.highlight_all_errors) break;
    }
  }

  return validationErrors.length === 0
    ? true
    : mergedConfig.error_display_html
      ? validationErrors
      : false;
};
